import { useAuth } from "@clerk/expo";
import { useCallback, useState } from "react";

import { useLocalization } from "@/hooks";

const useExportRunResults = (format: "docx" | "pdf" | "xlsx") => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { getToken } = useAuth();
  const { i18n, locale } = useLocalization();

  const exportResults = useCallback(
    async (studyId: string, runId: string) => {
      setError(null);
      setLoading(true);

      try {
        const token = await getToken();

        const response = await fetch(
          `${process.env.EXPO_PUBLIC_API_URL}/studies/${studyId}/runs/${runId}/exports/${format}`,
          {
            headers: {
              "Accept-Language": locale.replace("-", "_"),
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(i18n.t("misc.errorOccurred"));
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;

        const contentDisposition = response.headers.get("content-disposition");
        let filename = `run_results.${format}`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="([^"]+)"/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [format, getToken, i18n, locale]
  );

  return {
    error,
    exportResults,
    loading,
  };
};

export { useExportRunResults };
